import { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../../../../../../redux/store";
import VerbachatIcon from "../../../../../../../../../../../assets/svg/verbachat-icon";
import SearchIcon from "../../../../../../../../../../../assets/svg/search-icon";
import CloseIcon from "../../../../../../../../../../../assets/svg/close-icon";
import ReplyIcon from "../../../../../../../../../../../assets/svg/reply-icon";
import LeftArrowIcon from "../../../../../../../../../../../assets/svg/left-arrow-icon";
import ActivityCommentItem from "./ActivityCommentItem";
import ActivityEmailItem from "./ActivityEmailItem";
import { CommentAuthor } from "./CommentAuthor";
import DeleteCommentModal from "./DeleteCommentModal";
import {
  useGetTicketCommentsQuery,
  useDeleteTicketCommentMutation,
} from "../../../../../../../../../../../redux/services/helpdeskApi";
import {
  type TicketComment,
  type TicketMail,
  type TicketCommentCreatedBy,
  type ActivityItem,
} from "./CommentCard";
import AttachmentPreviewModal from "../../../../../../../../../../Communications/components/CommunicationDetailsPanel/components/AttachmentPreviewModal";
import { type AttachmentPreviewItem } from "../../../../../../../../../../Communications/utils/types";
import { useDebounce } from "../../../../../../../../../../../hooks/useDebounce";
import CircularLoader from "../../../../../../../../../../../components/Loader/CircularLoader";

const MODAL_COMMENT_CONTAINER_CLASS = "px-6 py-5";
const MODAL_EMAIL_CONTAINER_CLASS = "p-4";

const NOOP = () => {};

const resolveCreatedByName = (
  createdBy: TicketCommentCreatedBy | undefined
): string | undefined => {
  if (typeof createdBy === "object") return createdBy?.name;
  return undefined;
};

const resolveCreatedById = (
  createdBy: TicketCommentCreatedBy | undefined
): string | undefined =>
  typeof createdBy === "object" ? createdBy?.id : createdBy;

interface ActivityExpandedModalProps {
  ticketId?: number;
  sortOrder: "newest" | "oldest" | null;
  canUpdate?: boolean;
  onClose: () => void;
}

const ActivityExpandedModal = ({
  ticketId,
  sortOrder,
  canUpdate = true,
  onClose,
}: ActivityExpandedModalProps) => {
  const currentUserId = useSelector(
    (state: RootState) => state?.auth?.user?._id
  );
  const [searchValue, setSearchValue] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [previewImages, setPreviewImages] = useState<AttachmentPreviewItem[]>(
    []
  );

  const [expandedActivities, setExpandedActivities] = useState<Set<string>>(
    new Set()
  );
  const [overflowingActivities, setOverflowingActivities] = useState<
    Set<string>
  >(new Set());
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [pendingDeleteCommentId, setPendingDeleteCommentId] = useState<
    string | null
  >(null);
  const [isDeletingComment, setIsDeletingComment] = useState(false);
  const [replyingToComment, setReplyingToComment] =
    useState<TicketComment | null>(null);
  const [viewingMailThread, setViewingMailThread] = useState<TicketMail | null>(
    null
  );

  const searchInputRef = useRef<HTMLInputElement>(null);
  const contentRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const observersRef = useRef<Map<string, ResizeObserver>>(new Map());
  const refCallbacksCache = useRef<
    Map<string, (el: HTMLDivElement | null) => void>
  >(new Map());

  const debouncedSearch = useDebounce(searchValue, 400);

  const apiSortOrder = useMemo(() => {
    if (sortOrder === "newest") return "desc";
    if (sortOrder === "oldest") return "asc";
    return undefined;
  }, [sortOrder]);

  const { data: commentsData, isFetching: isCommentsFetching } =
    useGetTicketCommentsQuery(
      {
        ticket_id: String(ticketId),
        sort_order: apiSortOrder,
        search: debouncedSearch?.trim() || undefined,
      },
      { skip: !ticketId }
    );

  const [deleteTicketComment] = useDeleteTicketCommentMutation();

  const activityItems: ActivityItem[] = useMemo(() => {
    const merged: ActivityItem[] = [
      ...(
        (commentsData?.comments ?? commentsData ?? []) as TicketComment[]
      ).map((comment) => ({ ...comment, _type: "comment" as const })),
      ...((commentsData?.mails ?? []) as TicketMail[]).map((mail) => ({
        ...mail,
        _type: "mail" as const,
      })),
    ];
    merged.sort((a, b) => {
      const diff =
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      return sortOrder === "newest" ? -diff : diff;
    });
    return merged;
  }, [commentsData, sortOrder]);

  // -- Refs --

  const getContentRef = useCallback((id: string) => {
    let cb = refCallbacksCache.current?.get(id);
    if (!cb) {
      cb = (el: HTMLDivElement | null) => {
        const oldObserver = observersRef.current.get(id);
        if (oldObserver) {
          oldObserver.disconnect();
          observersRef.current.delete(id);
        }
        if (el) {
          contentRefs.current.set(id, el);
          const observer = new ResizeObserver(() => {
            const isOver = el.scrollHeight > el.clientHeight;
            if (isOver) {
              setOverflowingActivities((prev) => {
                if (prev.has(id)) return prev;
                const next = new Set(prev);
                next.add(id);
                return next;
              });
            }
          });
          observer.observe(el);
          observersRef.current.set(id, observer);
        } else {
          contentRefs.current?.delete(id);
        }
      };
      refCallbacksCache.current.set(id, cb);
    }
    return cb;
  }, []);

  // -- Handlers --

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(e?.target?.value || "");
    },
    []
  );

  const handleOverlayMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  const toggleActivityExpansion = useCallback((id: string) => {
    setExpandedActivities((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleViewCommentThread = useCallback((comment: TicketComment) => {
    setReplyingToComment(comment);
  }, []);

  const handleBackFromThread = useCallback(() => {
    setReplyingToComment(null);
  }, []);

  const handleBackFromMailThread = useCallback(() => {
    setViewingMailThread(null);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDeleteCommentId) return;
    setIsDeletingComment(true);
    try {
      await deleteTicketComment(pendingDeleteCommentId);
      setReplyingToComment((current) =>
        current !== null && current.id === pendingDeleteCommentId
          ? null
          : current
      );
      setPendingDeleteCommentId(null);
    } finally {
      setIsDeletingComment(false);
    }
  }, [pendingDeleteCommentId, deleteTicketComment]);

  const handleDeleteModalOpenChange = useCallback((open: boolean) => {
    if (!open) setPendingDeleteCommentId(null);
  }, []);

  const handleShowMailThread = useCallback((mail: TicketMail) => {
    setViewingMailThread(mail);
  }, []);

  const buildCommentImages = useCallback(
    (comment: TicketComment): AttachmentPreviewItem[] => {
      const attachmentImages: AttachmentPreviewItem[] = (
        comment?.attachments ?? []
      )
        .filter((att) => att?.content_type?.startsWith("image/"))
        .map((att) => ({
          s3_key: att.s3_key,
          s3_url: att.s3_url,
          file_name: att.file_name,
          content_type: att.content_type,
          timestamp: comment.created_at,
        }));

      const seen = new Set(attachmentImages?.map((a) => a.s3_url));
      const inlineImages: AttachmentPreviewItem[] = [];
      if (comment?.content) {
        const div = document.createElement("div");
        div.innerHTML = comment.content;
        div?.querySelectorAll("img").forEach((img) => {
          const src = img?.getAttribute("src") || "";
          if (src && !seen?.has(src)) {
            inlineImages?.push({
              s3_url: src,
              file_name: img?.getAttribute("alt") || "image",
              content_type: "image/",
              timestamp: comment.created_at,
            });
            seen?.add(src);
          }
        });
      }
      return [...attachmentImages, ...inlineImages];
    },
    []
  );

  const openPreview = useCallback(
    (comment: TicketComment, index: number) => {
      const images = buildCommentImages(comment);
      setPreviewImages(images);
      setPreviewIndex(index);
      setPreviewOpen(true);
    },
    [buildCommentImages]
  );

  const getCommentItemProps = useCallback(
    (comment: TicketComment) => ({
      comment,
      isExpanded: expandedActivities.has(comment.id),
      isOverflowing: overflowingActivities.has(comment.id),
      isEditing: false,
      isMenuOpen: openMenuId === comment.id,
      commentImages: buildCommentImages(comment),
      containerClassName: MODAL_COMMENT_CONTAINER_CLASS,
      hideEdit: true,
      canUpdate,
      isOwnComment: resolveCreatedById(comment?.created_by) === currentUserId,
      showReplyButton: false,
      contentRef: getContentRef(comment.id),
      onToggleExpansion: () => toggleActivityExpansion(comment.id),
      onReply: () => handleViewCommentThread(comment),
      onEditCancel: NOOP,
      onEditStart: NOOP,
      onDeleteRequest: () => {
        setPendingDeleteCommentId(comment.id);
        setOpenMenuId(null);
      },
      onMenuToggle: () =>
        setOpenMenuId((prev) => (prev === comment.id ? null : comment.id)),
      onMenuClose: () => setOpenMenuId(null),
      onAttachmentPreview: (index: number) => openPreview(comment, index),
    }),
    [
      expandedActivities,
      overflowingActivities,
      openMenuId,
      canUpdate,
      currentUserId,
      buildCommentImages,
      getContentRef,
      toggleActivityExpansion,
      handleViewCommentThread,
      openPreview,
    ]
  );

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // -- Thread views --

  const renderThreadView = useMemo(() => {
    if (replyingToComment && !isCommentsFetching) {
      const parentComment =
        (activityItems.find(
          (c) => c._type === "comment" && c.id === replyingToComment?.id
        ) as (TicketComment & { _type: "comment" }) | undefined) ??
        replyingToComment;

      const replyCount = parentComment?.replies?.length ?? 0;

      return (
        <>
          <div className="flex items-center justify-between px-3 py-2 border-b border-border-gray dark:border-border-gray-dk shrink-0">
            <button
              className="flex items-center gap-1 cursor-pointer hover:opacity-80 shrink-0"
              onClick={handleBackFromThread}
            >
              <LeftArrowIcon className="w-4 h-4 text-heading-text dark:text-heading-text-dk" />
              <span className="text-md font-medium text-heading-text dark:text-heading-text-dk">
                Back
              </span>
            </button>
            <div className="flex items-center gap-[6px] min-w-0 overflow-hidden">
              <span className="text-xs font-normal text-dark-gray dark:text-dark-gray-dk shrink-0">
                Thread by
              </span>
              <div className="flex items-center gap-[5px] min-w-0 overflow-hidden">
                <CommentAuthor
                  userName={resolveCreatedByName(parentComment?.created_by)}
                  avatarSize={20}
                  nameClassName="text-md font-medium text-heading-text dark:text-heading-text-dk truncate"
                />
              </div>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            <ActivityCommentItem
              {...getCommentItemProps(parentComment)}
              showReplyButton={false}
              showRepliesBar={false}
            />
            {replyCount > 0 && (
              <div className="flex items-center justify-center gap-1 py-2 bg-hover-effect dark:bg-hover-effect-dk border-b border-border-gray dark:border-border-gray-dk">
                <ReplyIcon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk" />
                <p className="text-xs font-medium text-verba-indigo dark:text-verba-indigo-dk">
                  {replyCount} {replyCount === 1 ? "Reply" : "Replies"}
                </p>
              </div>
            )}
            {parentComment?.replies?.map((reply) => (
              <ActivityCommentItem
                key={reply.id}
                {...getCommentItemProps(reply)}
                showReplyButton={false}
                showRepliesBar={false}
              />
            ))}
          </div>
        </>
      );
    }

    if (viewingMailThread && !isCommentsFetching) {
      const parentMail =
        (activityItems.find(
          (c) => c._type === "mail" && c.id === viewingMailThread?.id
        ) as (TicketMail & { _type: "mail" }) | undefined) ?? viewingMailThread;

      const threadReplyCount = parentMail?.replies?.length ?? 0;

      return (
        <>
          <div className="flex items-center justify-between px-3 py-2 border-b border-border-gray dark:border-border-gray-dk shrink-0">
            <button
              className="flex items-center gap-1 cursor-pointer hover:opacity-80 shrink-0"
              onClick={handleBackFromMailThread}
            >
              <LeftArrowIcon className="w-5 h-5 text-heading-text dark:text-heading-text-dk" />
              <span className="text-md font-medium text-heading-text dark:text-heading-text-dk">
                Back
              </span>
            </button>
            <div className="flex items-center gap-[6px] min-w-0 overflow-hidden">
              <span className="text-xs font-normal text-dark-gray dark:text-dark-gray-dk shrink-0">
                Thread by
              </span>
              <div className="flex items-center gap-[5px] min-w-0 overflow-hidden">
                <CommentAuthor
                  userName={resolveCreatedByName(parentMail?.created_by)}
                  avatarSize={20}
                  nameClassName="text-md font-medium text-heading-text dark:text-heading-text-dk truncate"
                />
              </div>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            <ActivityEmailItem
              mail={parentMail}
              containerClassName={MODAL_EMAIL_CONTAINER_CLASS}
            />
            {threadReplyCount > 0 && (
              <div className="flex items-center justify-center gap-1 py-2 bg-hover-effect dark:bg-hover-effect-dk border-b border-border-gray dark:border-border-gray-dk">
                <ReplyIcon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk" />
                <p className="text-xs font-medium text-verba-indigo dark:text-verba-indigo-dk">
                  {threadReplyCount}{" "}
                  {threadReplyCount === 1 ? "Email Reply" : "Email Replies"}
                </p>
              </div>
            )}
            {parentMail?.replies?.map((reply) => (
              <ActivityEmailItem
                key={reply.id}
                mail={reply}
                variant="compact"
                containerClassName={MODAL_EMAIL_CONTAINER_CLASS}
              />
            ))}
          </div>
        </>
      );
    }

    return null;
  }, [
    replyingToComment,
    viewingMailThread,
    isCommentsFetching,
    activityItems,
    getCommentItemProps,
    handleBackFromThread,
    handleBackFromMailThread,
  ]);

  const isInThread = !!replyingToComment || !!viewingMailThread;

  return createPortal(
    <div
      className="fixed inset-0 z-100 flex items-center justify-center bg-black/50 backdrop-blur-xs"
      onMouseDown={handleOverlayMouseDown}
    >
      <div className="w-[850px] h-[65vh] flex flex-col rounded-lg border border-border-gray dark:border-border-gray-dk bg-popper-bg-fixed-white dark:bg-popper-bg-fixed-white-dk shadow-popper-blur-effect backdrop-blur-[20px]">
        <div className="flex items-center justify-between h-[50px] px-3 border-b border-border-gray dark:border-border-gray-dk shrink-0">
          <div className="flex items-center gap-[18px]">
            <div className="flex items-center gap-1">
              <VerbachatIcon className="w-[18px] h-[18px] text-verba-indigo dark:text-verba-indigo-dk" />
              <span className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
                Activity
              </span>
            </div>
            {!isInThread && (
              <div className="flex items-center gap-1 px-1.5 h-[26px] w-[150px] border border-border-gray dark:border-border-gray-dk rounded-md bg-popper-bg dark:bg-popper-bg-dk">
                <SearchIcon className="w-4 h-4 text-verba-indigo dark:text-verba-indigo-dk shrink-0" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchValue}
                  onChange={handleSearchChange}
                  placeholder="Search"
                  className="text-xs font-normal text-verba-indigo dark:text-verba-indigo-dk placeholder:text-gray-placeholder dark:placeholder:text-gray-placeholder-dk bg-transparent border-none outline-none w-full"
                />
              </div>
            )}
          </div>
          <button
            className="shrink-0 w-[15px] h-[15px] bg-gray-100 dark:bg-tag-gray-dk rounded-full flex flex-col items-center justify-center transition-transform duration-200 hover:scale-110"
            onClick={onClose}
            aria-label="Close modal"
          >
            <CloseIcon className="w-[5.5px] h-[5.5px] text-verba-indigo dark:text-verba-indigo-dk" />
          </button>
        </div>

        {renderThreadView ? (
          renderThreadView
        ) : isCommentsFetching ? (
          <div className="flex-1 min-h-full flex items-center justify-center">
            <CircularLoader />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto min-h-0">
            {activityItems.length > 0 ? (
              activityItems.map((item) =>
                item._type === "mail" ? (
                  <ActivityEmailItem
                    key={`modal-mail-${item.id}`}
                    mail={item}
                    containerClassName={MODAL_EMAIL_CONTAINER_CLASS}
                    onShowThread={() => handleShowMailThread(item)}
                  />
                ) : (
                  <ActivityCommentItem
                    key={`modal-comment-${item.id}`}
                    {...getCommentItemProps(item)}
                  />
                )
              )
            ) : (
              <div className="flex-1 min-h-full flex items-center justify-center">
                <p className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
                  No activity found
                </p>
              </div>
            )}
          </div>
        )}

        <DeleteCommentModal
          open={!!pendingDeleteCommentId}
          onOpenChange={handleDeleteModalOpenChange}
          isDeleting={isDeletingComment}
          onConfirmDelete={handleConfirmDelete}
        />

        {previewImages?.length > 0 && (
          <AttachmentPreviewModal
            open={previewOpen}
            onOpenChange={setPreviewOpen}
            attachments={previewImages}
            initialIndex={previewIndex}
          />
        )}
      </div>
    </div>,
    document.body
  );
};

export default ActivityExpandedModal;
